import { defineNuxtRouteMiddleware, navigateTo, useRequestHeaders, useRuntimeConfig } from "#app";
import { getCurrentServer } from "@/utils/globalData";
import { useMainStore } from "~/composables/store";
import { querySassmallDealerShop } from "~/sassmall/api/site";
import {
  buildAffiliateH5RegisterUrl,
  isAffiliateRegisterPath,
  normalizeAffiliateInviteCode,
} from "~/utils/affiliateRegistration";
import { deviceType } from "~/utils/deviceType";

function isEnabledFlag(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }

  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "true" || normalized === "1";
}

function resolveMobileAppDownloadUrl(siteValue: unknown, query: Record<string, any> = {}) {
  const value = String(siteValue || "").trim();
  if (!value) return "";
  try {
    const base = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`);
    if (!/^https?:$/.test(base.protocol)) return "";
    base.pathname = "/h5/pages/loginSon/AppDownloadPage";
    base.search = "";
    for (const [key, rawValue] of Object.entries(query || {})) {
      if (rawValue == null || rawValue === "") continue;
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      for (const item of values) base.searchParams.append(key, String(item));
    }
    return base.toString();
  } catch {
    return "";
  }
}

function getConfiguredH5Site(mainStore: any, headers: Record<string, any>) {
  const current = mainStore?.getStoreInfoDataMap?.(getCurrentServer(headers)) || {};
  if (current?.h5SiteUrl) return current.h5SiteUrl;
  return Object.values(mainStore?.storeInfoDataMap || {})
    .map((item: any) => item?.h5SiteUrl)
    .find(Boolean) || "";
}

function firstHeaderValue(value: unknown) {
  return String(Array.isArray(value) ? value[0] : value || "").split(",")[0].trim();
}

function getRequestSiteUrl(headers: Record<string, any>) {
  if (!process.server) return window.location.origin;
  const host = firstHeaderValue(headers["x-forwarded-host"] || headers.host);
  if (!host) return "";
  const forwardedProtocol = firstHeaderValue(headers["x-forwarded-proto"]);
  const protocol = forwardedProtocol || (/^(?:localhost|127\.0\.0\.1)(?::|$)/i.test(host) ? "http" : "https");
  return `${protocol}://${host}`;
}

function getBootstrapH5Site(response: any) {
  return response?.data?.h5SiteUrl
    || response?.data?.otherJson?.h5SiteUrl
    || response?.data?.offiaccountFollowData?.otherJson?.h5SiteUrl
    || "";
}

export default defineNuxtRouteMiddleware(async (to) => {
  const requestHeaders = process.server
    ? useRequestHeaders(["user-agent", "host", "x-forwarded-host", "x-forwarded-proto"])
    : {};
  const userAgent = process.server
    ? (requestHeaders["user-agent"] || "")
    : navigator.userAgent;
  const currentDevice = deviceType(userAgent || "");
  const isPc = currentDevice?.type === "pc";
  const mainStore = useMainStore();

  if (isAffiliateRegisterPath(to.path) && !isPc) {
    let h5Site = "";
    try {
      const response = await querySassmallDealerShop({ url: getRequestSiteUrl(requestHeaders) });
      h5Site = getBootstrapH5Site(response);
    }
    catch (error) {
      console.error("Failed to resolve the H5 affiliate registration site", error);
    }
    h5Site ||= getConfiguredH5Site(mainStore, requestHeaders);
    const target = buildAffiliateH5RegisterUrl(
      h5Site,
      normalizeAffiliateInviteCode(to.query.inviteCode),
    );
    if (target) {
      if (!process.server) {
        window.location.replace(target);
        return;
      }
      return navigateTo(target, { external: true, redirectCode: 302, replace: true });
    }
  }

  const runtimeConfig = useRuntimeConfig();
  if (!runtimeConfig.public.isCmsSite) {
    return;
  }

  const mallSiteConfig = mainStore?.getMallSiteConfig?.(getCurrentServer()) || {};
  const disabledMobileSiteNavigate = isEnabledFlag(mallSiteConfig?.disabledMobileSiteNavigate);

  if (!isPc && disabledMobileSiteNavigate) {
    return;
  }

  if ((to.path === "/" || to.path === "") && !isPc) {
    if(!process.server){
      
      return window.location.href="/h5/"+ window.location.search;
    }
    return navigateTo({ path: "/h5/", query: to.query }, { redirectCode: 302, replace: true });
  }

  if (to.path === "/app-download" && !isPc) {
    const h5Site = getConfiguredH5Site(mainStore, requestHeaders);
    const target = resolveMobileAppDownloadUrl(h5Site, to.query);
    if (target) {
      if (!process.server) {
        window.location.replace(target);
        return;
      }
      return navigateTo(target, { external: true, redirectCode: 302, replace: true });
    }
  }

  if (to.path.includes("/apph5") && !isPc) {
    if(!process.server){
      return window.location.href="/apph5/"+ window.location.search;
    }
    return navigateTo({ path: "/apph5/", query: to.query }, { redirectCode: 302, replace: true });
  }

  if ((to.path === "/lch5" || to.path === "/lch5/") && !isPc) {
    if(!process.server){
      return window.location.href="/lch5/"+ window.location.search;
    }
    return navigateTo({ path: "/lch5/", query: to.query }, { redirectCode: 302, replace: true });
  }

  if (to.path.includes("/detail/pc") && !isPc) {
    if(!process.server){
      return window.location.href="/detail/"+ window.location.search;
    }
    return navigateTo({ path: "/detail", query: to.query }, { redirectCode: 302, replace: true });
  }
});
